import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  CheckCircle,
  ContentCopy,
  DeleteOutline,
  DragIndicator,
  ExpandLess,
  ExpandMore,
  HelpOutline,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";

export type ManualStudyAnswerType = "text" | "number" | "date";
export type ManualParticipantAnswerType =
  | ManualStudyAnswerType
  | "long_text"
  | "yes_no"
  | "single_choice"
  | "multiple_choice";
export type ManualAnswerType = ManualParticipantAnswerType;

export type ManualStudyQuestion = {
  id: string;
  prompt: string;
  answerType: ManualStudyAnswerType;
  answer: string;
};

export type ManualStudySection = {
  id: string;
  title: string;
  collapsed?: boolean;
  questions: ManualStudyQuestion[];
};

export type ManualParticipantQuestion = {
  id: string;
  prompt: string;
  answerType: ManualParticipantAnswerType;
  required: boolean;
  options?: string[];
};

export type ManualParticipantSection = {
  id: string;
  title: string;
  collapsed?: boolean;
  questions: ManualParticipantQuestion[];
};

export type ManualConsentState = {
  studySections: ManualStudySection[];
  participantSections: ManualParticipantSection[];
  consentStatement: string;
};

type BackendSection = {
  key: string;
  title: string;
  bodyHtml: string;
  order: number;
};

const STUDY_ANSWER_TYPE_OPTIONS: Array<{
  value: ManualStudyAnswerType;
  label: string;
}> = [
  { value: "text", label: "Text answer" },
  { value: "number", label: "Numerical value" },
  { value: "date", label: "Date picker" },
];

const PARTICIPANT_ANSWER_TYPE_OPTIONS: Array<{
  value: ManualParticipantAnswerType;
  label: string;
}> = [
  { value: "text", label: "Short text" },
  { value: "long_text", label: "Long text" },
  { value: "number", label: "Numerical value" },
  { value: "date", label: "Date picker" },
  { value: "yes_no", label: "Yes / No" },
  { value: "single_choice", label: "Single choice" },
  { value: "multiple_choice", label: "Multiple choice" },
];

const DEFAULT_CONSENT_STATEMENT =
  "I have read and understand the above information. I agree to participate in this study.";

function makeId(prefix: string) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function escapeHtml(input: unknown) {
  if (input === null || input === undefined) return "";
  return String(input)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function normalizeStudyAnswerType(value: unknown): ManualStudyAnswerType {
  if (value === "number" || value === "date") return value;
  return "text";
}

function normalizeParticipantAnswerType(value: unknown): ManualParticipantAnswerType {
  if (
    value === "number" ||
    value === "date" ||
    value === "long_text" ||
    value === "yes_no" ||
    value === "single_choice" ||
    value === "multiple_choice"
  ) {
    return value;
  }
  return "text";
}

function isChoiceAnswerType(type: ManualParticipantAnswerType) {
  return type === "single_choice" || type === "multiple_choice";
}

function normalizeOptions(input: unknown): string[] {
  return Array.isArray(input)
    ? input.map((option) => String(option || "").trim()).filter(Boolean)
    : [];
}

function defaultChoiceOptions() {
  return ["Option 1", "Option 2"];
}

function moveItem<T>(items: T[], index: number, direction: -1 | 1) {
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= items.length) return items;
  const next = items.slice();
  [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
  return next;
}

function studyQuestionFromInput(input: any, index: number): ManualStudyQuestion {
  return {
    id: String(input?.id || input?.key || makeId(`study_q_${index + 1}`)),
    prompt: String(input?.prompt || input?.question || input?.label || ""),
    answerType: normalizeStudyAnswerType(input?.answerType || input?.type),
    answer: String(input?.answer ?? input?.answerValue ?? input?.value ?? ""),
  };
}

function participantQuestionFromInput(
  input: any,
  index: number,
): ManualParticipantQuestion {
  return {
    id: String(input?.id || input?.key || makeId(`participant_q_${index + 1}`)),
    prompt: String(input?.prompt || input?.question || input?.label || ""),
    answerType: normalizeParticipantAnswerType(input?.answerType || input?.type),
    required: input?.required === true,
    options: normalizeOptions(input?.options),
  };
}

export function createDefaultManualState(): ManualConsentState {
  return {
    studySections: [
      {
        id: makeId("study_section"),
        title: "Study overview",
        collapsed: false,
        questions: [
          {
            id: makeId("study_q"),
            prompt: "",
            answerType: "text",
            answer: "",
          },
        ],
      },
    ],
    participantSections: [
      {
        id: makeId("participant_section"),
        title: "Participant questions",
        collapsed: false,
        questions: [],
      },
    ],
    consentStatement: DEFAULT_CONSENT_STATEMENT,
  };
}

export function manualStateFromFormContent(
  formContent: any,
  configuration: any,
): ManualConsentState {
  const fallback = createDefaultManualState();
  const sourceStudySections =
    formContent?.studySections || configuration?.studySections || [];
  const sourceParticipantSections =
    formContent?.participantSections || configuration?.participantSections || [];

  const studySections = Array.isArray(sourceStudySections)
    ? sourceStudySections.map((section: any, sectionIndex: number) => ({
        id: String(section?.id || section?.key || makeId("study_section")),
        title: String(section?.title || `Study section ${sectionIndex + 1}`),
        collapsed: false,
        questions: Array.isArray(section?.questions)
          ? section.questions.map(studyQuestionFromInput)
          : [],
      }))
    : fallback.studySections;

  const participantSections = Array.isArray(sourceParticipantSections)
    ? sourceParticipantSections.map((section: any, sectionIndex: number) => ({
        id: String(
          section?.id || section?.key || makeId("participant_section"),
        ),
        title: String(
          section?.title || `Participant section ${sectionIndex + 1}`,
        ),
        collapsed: false,
        questions: Array.isArray(section?.questions)
          ? section.questions.map(participantQuestionFromInput)
          : [],
      }))
    : fallback.participantSections;

  return {
    studySections: studySections.length ? studySections : fallback.studySections,
    participantSections,
    consentStatement:
      String(
        formContent?.consentStatement ||
          configuration?.consentStatement ||
          "",
      ) || fallback.consentStatement,
  };
}

export function normalizeManualState(state: ManualConsentState) {
  return {
    studySections: state.studySections.map((section, sectionIndex) => ({
      id: section.id,
      key: section.id,
      title: section.title.trim(),
      order: sectionIndex + 1,
      questions: section.questions.map((question, questionIndex) => ({
        id: question.id,
        key: question.id,
        prompt: question.prompt.trim(),
        answerType: question.answerType,
        answer: question.answer,
        order: questionIndex + 1,
      })),
    })),
    participantSections: state.participantSections.map(
      (section, sectionIndex) => ({
        id: section.id,
        key: section.id,
        title: section.title.trim(),
        order: sectionIndex + 1,
        questions: section.questions.map((question, questionIndex) => ({
          id: question.id,
          key: question.id,
          prompt: question.prompt.trim(),
          answerType: question.answerType,
          required: question.required,
          options: normalizeOptions(question.options),
          order: questionIndex + 1,
        })),
      }),
    ),
    consentStatement: state.consentStatement.trim() || DEFAULT_CONSENT_STATEMENT,
  };
}

export function manualSectionsToBackendSections(
  state: ManualConsentState,
): BackendSection[] {
  const normalized = normalizeManualState(state);
  return normalized.studySections.map((section, index) => {
    const rows = section.questions
      .map(
        (question) => `
          <div style="padding:10px 0; border-top:1px solid #eef2f7">
            <p style="margin:0 0 4px 0; color:#4b5563"><strong>${escapeHtml(question.prompt)}</strong></p>
            <p style="margin:0; color:#111827">${escapeHtml(question.answer)}</p>
          </div>`,
      )
      .join("");
    return {
      key: section.key,
      title: section.title,
      order: index + 1,
      bodyHtml: rows || "<p>No study details were provided.</p>",
    };
  });
}

export function validateManualConsentState(state: ManualConsentState) {
  const errors: Record<string, string> = {};
  const normalized = normalizeManualState(state);

  if (!normalized.studySections.length) {
    errors.manual = "Add at least one Study Q&A section.";
  }

  normalized.studySections.forEach((section, sectionIndex) => {
    if (!section.title) {
      errors[`studySectionTitle.${section.id}`] = "Section header is required";
    }
    if (!section.questions.length) {
      errors[`studySection.${section.id}`] =
        "Add at least one question-answer row";
    }
    section.questions.forEach((question) => {
      if (!question.prompt) {
        errors[`studyPrompt.${question.id}`] = "Question is required";
      }
      if (!question.answerType) {
        errors[`studyType.${question.id}`] = "Answer type is required";
      }
      if (String(question.answer ?? "").trim() === "") {
        errors[`studyAnswer.${question.id}`] =
          "Researcher answer is required";
      }
    });
    if (!section.title && sectionIndex === 0) {
      errors.manual = "Complete the required Study Q&A fields.";
    }
  });

  normalized.participantSections.forEach((section) => {
    if (section.questions.length && !section.title) {
      errors[`participantSectionTitle.${section.id}`] =
        "Section header is required";
    }
    section.questions.forEach((question) => {
      if (!question.prompt) {
        errors[`participantPrompt.${question.id}`] =
          "Participant question is required";
      }
      if (!question.answerType) {
        errors[`participantType.${question.id}`] = "Answer type is required";
      }
      if (
        isChoiceAnswerType(question.answerType) &&
        normalizeOptions(question.options).length < 2
      ) {
        errors[`participantOptions.${question.id}`] =
          "Add at least two choices";
      }
    });
  });

  if (!normalized.consentStatement) {
    errors.consentStatement = "Consent statement is required";
  }

  return errors;
}

function inputTypeForAnswerType(type: ManualStudyAnswerType) {
  if (type === "number") return "number";
  if (type === "date") return "date";
  return "text";
}

type ManualConsentBuilderProps = {
  state: ManualConsentState;
  onChange: (next: ManualConsentState) => void;
  errors: Record<string, string>;
  requireDrawnSignature: boolean;
  onRequireDrawnSignatureChange: (next: boolean) => void;
};

export default function ManualConsentBuilder({
  state,
  onChange,
  errors,
  requireDrawnSignature,
  onRequireDrawnSignatureChange,
}: ManualConsentBuilderProps) {
  const updateStudySections = (studySections: ManualStudySection[]) =>
    onChange({ ...state, studySections });
  const updateParticipantSections = (
    participantSections: ManualParticipantSection[],
  ) => onChange({ ...state, participantSections });

  const addStudySection = () =>
    updateStudySections([
      ...state.studySections,
      {
        id: makeId("study_section"),
        title: `Study section ${state.studySections.length + 1}`,
        collapsed: false,
        questions: [
          {
            id: makeId("study_q"),
            prompt: "",
            answerType: "text",
            answer: "",
          },
        ],
      },
    ]);

  const addParticipantSection = () =>
    updateParticipantSections([
      ...state.participantSections,
      {
        id: makeId("participant_section"),
        title: `Participant section ${state.participantSections.length + 1}`,
        collapsed: false,
        questions: [],
      },
    ]);

  const updateStudySection = (
    sectionId: string,
    patch: Partial<ManualStudySection>,
  ) =>
    updateStudySections(
      state.studySections.map((section) =>
        section.id === sectionId ? { ...section, ...patch } : section,
      ),
    );

  const updateParticipantSection = (
    sectionId: string,
    patch: Partial<ManualParticipantSection>,
  ) =>
    updateParticipantSections(
      state.participantSections.map((section) =>
        section.id === sectionId ? { ...section, ...patch } : section,
      ),
    );

  const updateStudyQuestion = (
    sectionId: string,
    questionId: string,
    patch: Partial<ManualStudyQuestion>,
  ) =>
    updateStudySections(
      state.studySections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              questions: section.questions.map((question) =>
                question.id === questionId
                  ? { ...question, ...patch }
                  : question,
              ),
            }
          : section,
      ),
    );

  const updateParticipantQuestion = (
    sectionId: string,
    questionId: string,
    patch: Partial<ManualParticipantQuestion>,
  ) =>
    updateParticipantSections(
      state.participantSections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              questions: section.questions.map((question) =>
                question.id === questionId
                  ? { ...question, ...patch }
                  : question,
              ),
            }
          : section,
      ),
    );

  const addStudyQuestion = (sectionId: string) =>
    updateStudySections(
      state.studySections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              collapsed: false,
              questions: [
                ...section.questions,
                {
                  id: makeId("study_q"),
                  prompt: "",
                  answerType: "text",
                  answer: "",
                },
              ],
            }
          : section,
      ),
    );

  const addParticipantQuestion = (sectionId: string) =>
    updateParticipantSections(
      state.participantSections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              collapsed: false,
              questions: [
                ...section.questions,
                {
                  id: makeId("participant_q"),
                  prompt: "",
                  answerType: "text",
                  required: false,
                  options: [],
                },
              ],
            }
          : section,
      ),
    );

  const updateParticipantOptions = (
    sectionId: string,
    questionId: string,
    options: string[],
  ) => updateParticipantQuestion(sectionId, questionId, { options });

  return (
    <Stack spacing={2.5}>
      {errors.manual && <Alert severity="error">{errors.manual}</Alert>}

      <Paper sx={{ p: 2.5, border: 1, borderColor: "divider" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Study Q&A
              </Typography>
              <Tooltip title="Use sections to group study information such as purpose, procedures, risks, privacy, contacts, or site-specific notes.">
                <HelpOutline fontSize="small" color="action" />
              </Tooltip>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Researchers complete these required answers while building the form. Participants see them as read-only study information.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={addStudySection}>
            Add section
          </Button>
        </Stack>
      </Paper>

      {state.studySections.map((section, sectionIndex) => (
        <Paper
          key={section.id}
          sx={{
            border: 1,
            borderColor: errors[`studySection.${section.id}`]
              ? "error.main"
              : "divider",
            overflow: "hidden",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "center" }}
            sx={{ p: 1.5, bgcolor: "background.default" }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title={section.collapsed ? "Expand" : "Collapse"}>
                <IconButton
                  size="small"
                  onClick={() =>
                    updateStudySection(section.id, {
                      collapsed: !section.collapsed,
                    })
                  }
                >
                  {section.collapsed ? <ExpandMore /> : <ExpandLess />}
                </IconButton>
              </Tooltip>
              <DragIndicator fontSize="small" color="disabled" />
            </Stack>
            <TextField
              label="Section header"
              value={section.title}
              onChange={(event) =>
                updateStudySection(section.id, { title: event.target.value })
              }
              required
              fullWidth
              error={Boolean(errors[`studySectionTitle.${section.id}`])}
              helperText={
                errors[`studySectionTitle.${section.id}`] ||
                "Section headers become the participant-facing information headings."
              }
            />
            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
              <Tooltip title="Move section up">
                <span>
                  <IconButton
                    size="small"
                    disabled={sectionIndex === 0}
                    onClick={() =>
                      updateStudySections(
                        moveItem(state.studySections, sectionIndex, -1),
                      )
                    }
                  >
                    <KeyboardArrowUp />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move section down">
                <span>
                  <IconButton
                    size="small"
                    disabled={sectionIndex === state.studySections.length - 1}
                    onClick={() =>
                      updateStudySections(
                        moveItem(state.studySections, sectionIndex, 1),
                      )
                    }
                  >
                    <KeyboardArrowDown />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remove section">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={state.studySections.length === 1}
                    onClick={() =>
                      updateStudySections(
                        state.studySections.filter((item) => item.id !== section.id),
                      )
                    }
                  >
                    <DeleteOutline />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
          <Collapse in={!section.collapsed} timeout="auto" unmountOnExit>
            <Stack spacing={1.5} sx={{ p: 2 }}>
              {errors[`studySection.${section.id}`] && (
                <Alert severity="error">{errors[`studySection.${section.id}`]}</Alert>
              )}
              {section.questions.map((question, questionIndex) => (
                <Paper
                  key={question.id}
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: 2 }}
                >
                  <Grid container spacing={1.5} alignItems="flex-start">
                    <Grid item xs={12} md={5}>
                      <TextField
                        label="Question"
                        value={question.prompt}
                        onChange={(event) =>
                          updateStudyQuestion(section.id, question.id, {
                            prompt: event.target.value,
                          })
                        }
                        fullWidth
                        required
                        error={Boolean(errors[`studyPrompt.${question.id}`])}
                        helperText={errors[`studyPrompt.${question.id}`]}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Answer type"
                        value={question.answerType}
                        onChange={(event) =>
                          updateStudyQuestion(section.id, question.id, {
                            answerType: event.target.value as ManualStudyAnswerType,
                            answer: "",
                          })
                        }
                        fullWidth
                        required
                        select
                      >
                        {STUDY_ANSWER_TYPE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Researcher answer"
                        value={question.answer}
                        onChange={(event) =>
                          updateStudyQuestion(section.id, question.id, {
                            answer: event.target.value,
                          })
                        }
                        fullWidth
                        required
                        multiline={question.answerType === "text"}
                        rows={question.answerType === "text" ? 3 : undefined}
                        type={inputTypeForAnswerType(question.answerType)}
                        InputLabelProps={
                          question.answerType === "date" ? { shrink: true } : undefined
                        }
                        error={Boolean(errors[`studyAnswer.${question.id}`])}
                        helperText={errors[`studyAnswer.${question.id}`]}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Move row up">
                          <span>
                            <IconButton
                              size="small"
                              disabled={questionIndex === 0}
                              onClick={() =>
                                updateStudySection(section.id, {
                                  questions: moveItem(
                                    section.questions,
                                    questionIndex,
                                    -1,
                                  ),
                                })
                              }
                            >
                              <KeyboardArrowUp />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move row down">
                          <span>
                            <IconButton
                              size="small"
                              disabled={
                                questionIndex === section.questions.length - 1
                              }
                              onClick={() =>
                                updateStudySection(section.id, {
                                  questions: moveItem(
                                    section.questions,
                                    questionIndex,
                                    1,
                                  ),
                                })
                              }
                            >
                              <KeyboardArrowDown />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Duplicate row">
                          <IconButton
                            size="small"
                            onClick={() => {
                              const nextQuestions = section.questions.slice();
                              nextQuestions.splice(questionIndex + 1, 0, {
                                ...question,
                                id: makeId("study_q"),
                              });
                              updateStudySection(section.id, {
                                questions: nextQuestions,
                              });
                            }}
                          >
                            <ContentCopy />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove row">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={section.questions.length === 1}
                              onClick={() =>
                                updateStudySection(section.id, {
                                  questions: section.questions.filter(
                                    (item) => item.id !== question.id,
                                  ),
                                })
                              }
                            >
                              <DeleteOutline />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => addStudyQuestion(section.id)}
                sx={{ alignSelf: "flex-start" }}
              >
                Add Q&A row
              </Button>
            </Stack>
          </Collapse>
        </Paper>
      ))}

      <Paper sx={{ p: 2.5, border: 1, borderColor: "divider" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Participant Questions
              </Typography>
              <Tooltip title="Use this area only for answers the participant must provide. Agreement and typed signature are added automatically below.">
                <HelpOutline fontSize="small" color="action" />
              </Tooltip>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              These fields appear in the participant link. Each custom question can be optional or required.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={addParticipantSection}
          >
            Add section
          </Button>
        </Stack>
      </Paper>

      {state.participantSections.length === 0 && (
        <Alert severity="info">
          No custom participant sections are configured. Participants will still see the required agreement checkbox and typed signature.
        </Alert>
      )}

      {state.participantSections.map((section, sectionIndex) => (
        <Paper key={section.id} sx={{ border: 1, borderColor: "divider" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "center" }}
            sx={{ p: 1.5, bgcolor: "background.default" }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title={section.collapsed ? "Expand" : "Collapse"}>
                <IconButton
                  size="small"
                  onClick={() =>
                    updateParticipantSection(section.id, {
                      collapsed: !section.collapsed,
                    })
                  }
                >
                  {section.collapsed ? <ExpandMore /> : <ExpandLess />}
                </IconButton>
              </Tooltip>
              <DragIndicator fontSize="small" color="disabled" />
            </Stack>
            <TextField
              label="Section header"
              value={section.title}
              onChange={(event) =>
                updateParticipantSection(section.id, {
                  title: event.target.value,
                })
              }
              fullWidth
              error={Boolean(errors[`participantSectionTitle.${section.id}`])}
              helperText={
                errors[`participantSectionTitle.${section.id}`] ||
                "Section headers group related participant questions."
              }
            />
            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
              <Tooltip title="Move section up">
                <span>
                  <IconButton
                    size="small"
                    disabled={sectionIndex === 0}
                    onClick={() =>
                      updateParticipantSections(
                        moveItem(state.participantSections, sectionIndex, -1),
                      )
                    }
                  >
                    <KeyboardArrowUp />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move section down">
                <span>
                  <IconButton
                    size="small"
                    disabled={
                      sectionIndex === state.participantSections.length - 1
                    }
                    onClick={() =>
                      updateParticipantSections(
                        moveItem(state.participantSections, sectionIndex, 1),
                      )
                    }
                  >
                    <KeyboardArrowDown />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remove section">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() =>
                    updateParticipantSections(
                      state.participantSections.filter(
                        (item) => item.id !== section.id,
                      ),
                    )
                  }
                >
                  <DeleteOutline />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Collapse in={!section.collapsed} timeout="auto" unmountOnExit>
            <Stack spacing={1.5} sx={{ p: 2 }}>
              {section.questions.length === 0 ? (
                <Alert severity="info">
                  No custom participant questions yet. Add one below, or leave this section empty and remove it.
                </Alert>
              ) : (
                section.questions.map((question, questionIndex) => (
                  <Paper
                    key={question.id}
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: 2 }}
                  >
                    <Grid container spacing={1.5} alignItems="flex-start">
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Participant question"
                          value={question.prompt}
                          onChange={(event) =>
                            updateParticipantQuestion(section.id, question.id, {
                              prompt: event.target.value,
                            })
                          }
                          fullWidth
                          required
                          error={Boolean(
                            errors[`participantPrompt.${question.id}`],
                          )}
                          helperText={errors[`participantPrompt.${question.id}`]}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          label="Answer type"
                          value={question.answerType}
                          onChange={(event) => {
                            const answerType = event.target.value as ManualParticipantAnswerType;
                            updateParticipantQuestion(section.id, question.id, {
                              answerType,
                              options: isChoiceAnswerType(answerType)
                                ? normalizeOptions(question.options).length >= 2
                                  ? question.options
                                  : defaultChoiceOptions()
                                : [],
                            });
                          }}
                          fullWidth
                          required
                          select
                        >
                          {PARTICIPANT_ANSWER_TYPE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={1}
                          >
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Required
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Participant must answer
                              </Typography>
                            </Box>
                            <Switch
                              checked={question.required}
                              onChange={(event) =>
                                updateParticipantQuestion(
                                  section.id,
                                  question.id,
                                  { required: event.target.checked },
                                )
                              }
                            />
                          </Stack>
                        </Paper>
                      </Grid>
                      {isChoiceAnswerType(question.answerType) && (
                        <Grid item xs={12}>
                          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                            <Stack spacing={1}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Choices
                              </Typography>
                              {(question.options || defaultChoiceOptions()).map((option, optionIndex) => (
                                <Stack
                                  key={`${question.id}_option_${optionIndex}`}
                                  direction={{ xs: "column", sm: "row" }}
                                  spacing={1}
                                  alignItems={{ xs: "stretch", sm: "center" }}
                                >
                                  <TextField
                                    label={`Choice ${optionIndex + 1}`}
                                    value={option}
                                    onChange={(event) => {
                                      const nextOptions = (question.options || defaultChoiceOptions()).slice();
                                      nextOptions[optionIndex] = event.target.value;
                                      updateParticipantOptions(section.id, question.id, nextOptions);
                                    }}
                                    size="small"
                                    fullWidth
                                  />
                                  <Tooltip title="Remove choice">
                                    <span>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        disabled={(question.options || defaultChoiceOptions()).length <= 2}
                                        onClick={() => {
                                          const nextOptions = (question.options || defaultChoiceOptions()).filter(
                                            (_, index) => index !== optionIndex,
                                          );
                                          updateParticipantOptions(section.id, question.id, nextOptions);
                                        }}
                                      >
                                        <DeleteOutline />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Stack>
                              ))}
                              {errors[`participantOptions.${question.id}`] && (
                                <Typography variant="caption" color="error">
                                  {errors[`participantOptions.${question.id}`]}
                                </Typography>
                              )}
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Add />}
                                onClick={() =>
                                  updateParticipantOptions(section.id, question.id, [
                                    ...(question.options || defaultChoiceOptions()),
                                    `Option ${(question.options || defaultChoiceOptions()).length + 1}`,
                                  ])
                                }
                                sx={{ alignSelf: "flex-start" }}
                              >
                                Add choice
                              </Button>
                            </Stack>
                          </Paper>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Move question up">
                            <span>
                              <IconButton
                                size="small"
                                disabled={questionIndex === 0}
                                onClick={() =>
                                  updateParticipantSection(section.id, {
                                    questions: moveItem(
                                      section.questions,
                                      questionIndex,
                                      -1,
                                    ),
                                  })
                                }
                              >
                                <KeyboardArrowUp />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move question down">
                            <span>
                              <IconButton
                                size="small"
                                disabled={
                                  questionIndex === section.questions.length - 1
                                }
                                onClick={() =>
                                  updateParticipantSection(section.id, {
                                    questions: moveItem(
                                      section.questions,
                                      questionIndex,
                                      1,
                                    ),
                                  })
                                }
                              >
                                <KeyboardArrowDown />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Duplicate question">
                            <IconButton
                              size="small"
                              onClick={() => {
                                const nextQuestions = section.questions.slice();
                                nextQuestions.splice(questionIndex + 1, 0, {
                                  ...question,
                                  id: makeId("participant_q"),
                                  options: question.options ? question.options.slice() : [],
                                });
                                updateParticipantSection(section.id, {
                                  questions: nextQuestions,
                                });
                              }}
                            >
                              <ContentCopy />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove question">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                updateParticipantSection(section.id, {
                                  questions: section.questions.filter(
                                    (item) => item.id !== question.id,
                                  ),
                                })
                              }
                            >
                              <DeleteOutline />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Paper>
                ))
              )}
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => addParticipantQuestion(section.id)}
                sx={{ alignSelf: "flex-start" }}
              >
                Add participant question
              </Button>
            </Stack>
          </Collapse>
        </Paper>
      ))}

      <Paper sx={{ p: 2.5, border: 1, borderColor: "divider" }}>
        <Stack spacing={2}>
          <Box>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Consent and signature presets
              </Typography>
              <Tooltip title="These controls keep every manual consent form consistent while still allowing custom participant questions above.">
                <HelpOutline fontSize="small" color="action" />
              </Tooltip>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              These participant controls are always included in manual consent forms.
            </Typography>
          </Box>
          <TextField
            label="Consent statement"
            value={state.consentStatement}
            onChange={(event) =>
              onChange({ ...state, consentStatement: event.target.value })
            }
            fullWidth
            multiline
            rows={3}
            required
            error={Boolean(errors.consentStatement)}
            helperText={errors.consentStatement}
          />
          <Divider />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              icon={<CheckCircle />}
              label="Agreement checkbox locked"
              color="primary"
              variant="outlined"
            />
            <Chip
              icon={<CheckCircle />}
              label="Typed signature required"
              color="primary"
              variant="outlined"
            />
          </Stack>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={1.5}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Show drawn signature box
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  When enabled, participants must draw a signature in addition
                  to typing their legal name.
                </Typography>
              </Box>
              <Switch
                checked={requireDrawnSignature}
                onChange={(event) =>
                  onRequireDrawnSignatureChange(event.target.checked)
                }
              />
            </Stack>
          </Paper>
        </Stack>
      </Paper>
    </Stack>
  );
}
